/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { LearnerProfile_Course as Course, Partner_Partner as Partner } from '__generated__/graphql-types';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { LONG_MONTH_YEAR_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Link, Typography2 } from '@coursera/cds-core';
import { CurriculumIcon } from '@coursera/cds-icons';

import SkillTags from 'bundles/account-profile/components/recruiting-profile-viewer/components/credentials/SkillTags';
import { getCourseVerificationUrl } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/courseUtils';

import _t from 'i18n!nls/account-profile';

const styles = {
  itemWrapper: css`
    display: flex;
    gap: var(--cds-spacing-300);
  `,

  iconWrapper: css`
    height: 64px;
    width: 64px;
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    background-color: var(--cds-color-neutral-background-primary-weak);
    border-radius: 3px;
  `,

  detailsWrapper: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
    flex-grow: 1;
    position: relative;
  `,
};

type PropsFromCaller = {
  course: Course;
  partner?: Pick<Partner, 'squareLogo' | 'name'>;
};

const CoursesSectionItem = ({ course, partner }: PropsFromCaller) => {
  const verifyUrl = getCourseVerificationUrl(course.verifyCode);
  return (
    <div css={styles.itemWrapper}>
      <div css={styles.iconWrapper}>
        {partner && partner.squareLogo ? (
          <Imgix maxHeight={64} maxWidth={64} src={partner.squareLogo} alt={`${partner.name}_logo`} />
        ) : (
          <CurriculumIcon height={72} width={72} />
        )}
      </div>
      <div css={styles.detailsWrapper}>
        {partner && (
          <Typography2 component="p" variant="bodySecondary" color="supportText">
            <FormattedMessage message={_t('{partnerName} · Course')} partnerName={partner.name} />
          </Typography2>
        )}
        <Typography2 component="h3" variant="subtitleMedium">
          {course.name}
        </Typography2>
        <SkillTags skillNames={course.skillNames} />
        {verifyUrl && (
          <Typography2 component="p" variant="bodySecondary">
            <Link href={verifyUrl} variant="standard" target="_blank">
              {_t('View certificate')}
            </Link>
          </Typography2>
        )}
        <Typography2 component="p" variant="bodySecondary" color="supportText">
          <FormattedMessage
            message={_t('Completed {formattedCompletionDate}')}
            formattedCompletionDate={formatDateTimeDisplay(course.completionDate, LONG_MONTH_YEAR_DISPLAY)}
          />
        </Typography2>
      </div>
    </div>
  );
};

export default CoursesSectionItem;
